import { useMemo } from 'react';
import { useBanks } from '../../../hooks/useBanks';
import { useSafes } from '../../../hooks/useSafes';
import { useBranches } from '../../../hooks/useBranches';
import { useEmployees } from '../../../hooks/useEmployees';
import { useProviders } from '../../../hooks/useProviders';
import { useBills } from '../../../hooks/useBills';
import { useCurrencies } from '../../../hooks/useCurrencies';
import { resolveCurrencyCode } from '../../../utils/currencyUtils';

export function useOutTransactionOptions(editedBill?: any) {
  const { data: banks, isLoading: banksLoading } = useBanks({ populate: '*' });
  const { data: safes, isLoading: safesLoading } = useSafes({ populate: '*' });
  const { data: branches, isLoading: branchesLoading } = useBranches({ populate: '*' });
  const { data: employees, isLoading: employeesLoading } = useEmployees({ populate: '*' });
  const { data: providers, isLoading: providersLoading } = useProviders({ populate: '*' });
  const { data: bills, isLoading: billsLoading } = useBills({ populate: '*' });
  const { data: currencies, isLoading: currenciesLoading } = useCurrencies({ populate: '*' });

  const bankOptions = useMemo(() => {
    if (!banks || !Array.isArray(banks)) return [];
    return banks
      .filter((bank: any) => bank.isActive ?? true)
      .map((bank: any) => ({
        value: bank.documentId || bank.id?.toString(),
        label: `${bank.name || ''} - ${bank.accountNumber || ''}`
      }));
  }, [banks]);

  const safeOptions = useMemo(() => {
    if (!safes || !Array.isArray(safes)) return [];
    return safes
      .filter((safe: any) => safe.isActive ?? true)
      .map((safe: any) => ({
        value: safe.documentId || safe.id?.toString(),
        label: `${safe.name || ''} - ${safe.location || ''}`
      }));
  }, [safes]);

  const branchOptions = useMemo(() => {
    if (!branches || !Array.isArray(branches)) return [];
    return branches
      .filter((branch: any) => branch.status === 'active')
      .map((branch: any) => ({
        value: branch.documentId || branch.id?.toString(),
        label: `${branch.name || ''} (${branch.type || 'office'})`
      }));
  }, [branches]);

  const employeeOptions = useMemo(() => {
    if (!employees || !Array.isArray(employees)) return [];
    return employees
      .filter((emp: any) => emp.status === 'active')
      .map((emp: any) => ({
        value: emp.documentId || emp.id?.toString(),
        label: `${emp.name || ''} - ${emp.code || ''}`
      }));
  }, [employees]);

  const providerOptions = useMemo(() => {
    if (!providers || !Array.isArray(providers)) return [];
    return providers
      .filter((provider: any) => provider.status === 'active')
      .map((provider: any) => ({
        value: provider.documentId || provider.id?.toString(),
        label: provider.name || ''
      }));
  }, [providers]);

  const billOptions = useMemo(() => {
    if (!bills || !Array.isArray(bills)) return [];
    const toOption = (bill: any) => ({
      value: bill.documentId || bill.id?.toString(),
      label: `Bill #${bill.id} - ${bill.description || ''} (${bill.amount || 0} ${resolveCurrencyCode(bill.currency)})`
    });
    const options = bills
      .filter((bill: any) => {
        const status = bill.status;
        return status === 'pending' || status === 'overdue' || status === 'partially_paid';
      })
      .map(toOption);
    // When editing, make sure the transaction's linked bill is selectable even
    // if its status no longer passes the "open bills" filter (e.g. paid)
    if (editedBill && typeof editedBill === 'object') {
      const editedValue = editedBill.documentId || editedBill.id?.toString();
      if (editedValue && !options.some((o: any) => o.value === editedValue)) {
        const full = bills.find((b: any) => (b.documentId || b.id?.toString()) === editedValue);
        options.unshift(toOption(full || editedBill));
      }
    }
    return options;
  }, [bills, editedBill]);

  const currencyOptions = useMemo(() => {
    if (!currencies || !Array.isArray(currencies) || currencies.length === 0) {
      return [];
    }
    return currencies
      .filter((currency: any) => currency.isActive !== false)
      .map((currency: any) => ({
        value: currency.documentId || currency.id || '',
        label: `${currency.code || ''} (${currency.name || currency.code || 'Unknown'})`
      }))
      .filter((option: any) => option.value);
  }, [currencies]);

  const isLoading = banksLoading || safesLoading || branchesLoading ||
    employeesLoading || providersLoading || billsLoading || currenciesLoading;

  return {
    banks,
    safes,
    currencies,
    branches,
    employees,
    providers,
    bills,
    bankOptions,
    safeOptions,
    branchOptions,
    employeeOptions,
    providerOptions,
    billOptions,
    currencyOptions,
    isLoading,
    banksLoading,
    safesLoading,
    branchesLoading,
    employeesLoading,
    providersLoading,
    billsLoading,
  };
}
